import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import chevronDown from 'assets/chevron-down.svg';
import ellipsis from 'assets/ellipsis.svg';

import { MEMBER_ROLES } from 'services/membersService';

import { formatDate } from 'utlis/string_format';

import MemberAvatar from './MemberAvatar';

import styles from './MemberRow.module.scss';

const ROLE_DISPLAY = {
  [MEMBER_ROLES.OWNER]: 'Owner',
  [MEMBER_ROLES.EDITOR]: 'Editor',
  [MEMBER_ROLES.VIEW_ONLY]: 'View only',
  [MEMBER_ROLES.PENDING]: 'Pending',
};

const CHANGEABLE_ROLES = [
  { value: MEMBER_ROLES.VIEW_ONLY, label: ROLE_DISPLAY[MEMBER_ROLES.VIEW_ONLY] },
  { value: MEMBER_ROLES.EDITOR, label: ROLE_DISPLAY[MEMBER_ROLES.EDITOR] },
];

const MemberRow = ({ member, isOwner, currentUserEmail, onRoleChange, onRemove }) => {
  const isOwnerRow = member.role === MEMBER_ROLES.OWNER;
  const canManage = isOwner && !isOwnerRow;
  const isCurrentUser = member.email === currentUserEmail;

  const [isRoleMenuOpen, setIsRoleMenuOpen] = useState(false);
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const roleRef = useRef(null);
  const actionsRef = useRef(null);

  useEffect(() => {
    const onClickOutside = e => {
      if (roleRef.current && !roleRef.current.contains(e.target)) {
        setIsRoleMenuOpen(false);
      }

      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setIsActionsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);

    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const isPending = member.role === MEMBER_ROLES.PENDING || member.status === MEMBER_ROLES.PENDING;

  return (
    <div className={styles.row}>
      {/* Name + Avatar */}
      <div className={styles.nameCell}>
        <MemberAvatar member={member} />
        <div className={styles.nameText}>
          <div className={styles.nameTitleRow}>
            {member.full_name && (
              <span className={styles.nameTitle}>
                {member.full_name}
              </span>
            )}
            {isCurrentUser && (
              <span className={styles.youLabel}>
                (You)
              </span>
            )}
          </div>
          <span className={styles.nameEmail}>
            {member.email}
          </span>
        </div>
      </div>

      {/* Permissions */}
      <div className={styles.permissionsCell}>
        {canManage && !isPending
          ? (
            <div className={styles.roleSelect} ref={roleRef}>
              <button
                type="button"
                className={styles.roleTrigger}
                onClick={() => setIsRoleMenuOpen(prev => !prev)}
              >
                <span className={styles.roleTriggerText}>
                  {ROLE_DISPLAY[member.role] ?? member.role}
                </span>
                <img src={chevronDown} alt="dropdown" />
              </button>

              {isRoleMenuOpen && (
                <div className={styles.roleMenu}>
                  {CHANGEABLE_ROLES.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      className={[
                        styles.roleMenuItem,
                        member.role === option.value ? styles.roleMenuItemActive : '',
                      ].join(' ')}
                      onClick={() => {
                        setIsRoleMenuOpen(false);
                        onRoleChange(member, option.value);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
          : (
            <span className={`${styles.roleLabel} ${isPending ? styles.roleLabelPending : ''}`}>
              {ROLE_DISPLAY[member.role] ?? member.role}
            </span>
          )}
      </div>

      {/* Last active */}
      <div className={styles.lastActiveCell}>
        <span className={styles.lastActiveText}>
          {member.last_active_at ? formatDate(member.last_active_at) : '—'}
        </span>
      </div>

      <div className={styles.lastActiveCell}>
        <span className={styles.lastActiveText}>
          {member.audits_count ? member.audits_count : '—'}
        </span>
      </div>

      {/* Actions cell — owner only. Joined members can be removed; pending invites can be cancelled. */}
      <div className={styles.actionsCell}>
        {canManage && (
          <div className={styles.actionsSelect} ref={actionsRef}>
            <button
              type="button"
              className={styles.actionsTrigger}
              onClick={() => setIsActionsMenuOpen(prev => !prev)}
            >
              <img src={ellipsis} alt="ellipsis" />
            </button>

            {isActionsMenuOpen && (
              <div className={styles.actionsMenu}>
                <button
                  type="button"
                  className={styles.actionsMenuItem}
                  onClick={() => {
                    setIsActionsMenuOpen(false);
                    onRemove(member);
                  }}
                >
                  {isPending ? 'Cancel invite' : 'Remove member'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

MemberRow.propTypes = {
  member: PropTypes.shape({
    uuid: PropTypes.string,
    email: PropTypes.string.isRequired,
    full_name: PropTypes.string,
    status: PropTypes.string,
    role: PropTypes.string,
    last_active_at: PropTypes.string,
    audits_count: PropTypes.number,
  }).isRequired,
  isOwner: PropTypes.bool,
  currentUserEmail: PropTypes.string,
  onRoleChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

MemberRow.defaultProps = {
  isOwner: false,
  currentUserEmail: null,
};

export default MemberRow;
